/* ============================================================================
   DÍA DE VUELO — varios vuelos, un total.

   Cada arranque de motor es UN vuelo. Un día de trabajo tiene varios, y lo que
   la bitácora y el status de mantenimiento necesitan es el total de la jornada:
   horas, aterrizajes, arranques y ciclos.

   QUÉ MIDE LA APLICACIÓN Y QUÉ NO
   -------------------------------
   El cronómetro mide tiempo y cuenta aterrizajes: eso lo sabe con certeza,
   porque lo marcó el piloto tramo a tramo.

   Los ciclos NG y NF NO se pueden deducir del reloj. Dependen del régimen del
   motor y se leen del indicador o se cuentan según el manual. La aplicación no
   los inventa ni los rellena con una regla supuesta —"un arranque, un ciclo"
   es una convención frecuente, pero no es universal y no está en ningún
   documento que el operador haya aportado—. Se piden al piloto, y si faltan se
   dice que faltan.

   Un ciclo mal contado desfasa el remanente de los componentes de vida límite,
   y ese error no se nota hasta la inspección.
   ========================================================================== */

const num = (v) => (Number.isFinite(Number(v)) && v !== "" && v !== null ? Number(v) : null);

/** Vuelo nuevo a partir del resultado del cronómetro. */
export function vueloDesdeCronometro(r, extra = {}) {
  return {
    id: `vuelo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    fecha: new Date().toISOString(),
    // El cronómetro sabe estos tres con certeza: los marcó el piloto.
    ftMs: r?.ftMs ?? 0,
    atMs: r?.atMs ?? 0,
    tierraMs: r?.tierraMs ?? 0,
    aterrizajes: r?.aterrizajes ?? 0,
    // Un arranque por vuelo, por definición: el vuelo empieza al arrancar.
    arranques: 1,
    // Estos NO salen del reloj. Los carga el piloto.
    ngCiclos: null,
    nfCiclos: null,
    // Combustible a bordo al arrancar y al apagar, en LITROS. La unidad con
    // que se escribe se elige en pantalla; el dato se guarda siempre en
    // litros, como en el resto de la aplicación, para que cambiar de unidad
    // no arrastre redondeo.
    combustibleInicialL: null,
    combustibleFinalL: null,
    observaciones: "",
    anulado: false,
    motivoAnulacion: null,
    ...extra,
  };
}

/**
 * Totales de la jornada.
 *
 * Los vuelos anulados no suman, pero se cuentan aparte: un vuelo que
 * desaparece del total sin dejar rastro es un vuelo que nadie puede auditar.
 */
export function resumenDelDia(vuelos) {
  const lista = Array.isArray(vuelos) ? vuelos.filter((v) => v && typeof v === "object") : [];
  const validos = lista.filter((v) => !v.anulado);
  const anulados = lista.length - validos.length;

  const total = validos.reduce((a, v) => ({
    ftMs: a.ftMs + (Number.isFinite(v.ftMs) ? v.ftMs : 0),
    atMs: a.atMs + (Number.isFinite(v.atMs) ? v.atMs : 0),
    tierraMs: a.tierraMs + (Number.isFinite(v.tierraMs) ? v.tierraMs : 0),
    aterrizajes: a.aterrizajes + (Number.isFinite(v.aterrizajes) ? v.aterrizajes : 0),
    arranques: a.arranques + (Number.isFinite(v.arranques) ? v.arranques : 0),
  }), { ftMs: 0, atMs: 0, tierraMs: 0, aterrizajes: 0, arranques: 0 });

  /**
   * Combustible consumido por vuelo: lo que había al arrancar menos lo que
   * quedó al apagar.
   *
   * Si entre dos vuelos se reabasteció, el inicial del siguiente será mayor
   * que el final del anterior. Eso es normal y no se toca: cada vuelo se mide
   * contra sí mismo.
   */
  const consumoDe = (v) => {
    const i = num(v.combustibleInicialL);
    const f = num(v.combustibleFinalL);
    if (i === null || f === null) return null;
    return i - f;
  };
  const consumos = validos.map(consumoDe);
  const conConsumo = consumos.filter((c) => c !== null);
  const consumosNegativos = conConsumo.filter((c) => c < 0).length;

  /**
   * Un consumo negativo NO se suma al total.
   *
   * Que un vuelo termine con más combustible del que arrancó significa que se
   * reabasteció con el motor en marcha, o que un número está mal cargado. En
   * los dos casos, ESE vuelo no permite medir cuánto se gastó: los dos números
   * describen otra cosa.
   *
   * Sumarlo con signo daba totales sin sentido —dos vuelos, uno de +10 y otro
   * de 3, daban un consumo del día de −7 L— y un consumo negativo no existe.
   * Se trata como dato faltante: el total no se cierra y se dice por qué.
   */
  const medibles = conConsumo.filter((c) => c >= 0);
  const consumoCompleto = validos.length > 0 && medibles.length === validos.length;

  // Los ciclos se suman solo si TODOS los vuelos los tienen. Un total parcial
  // sería menor que el real, que es la dirección peligrosa: haría creer que a
  // los componentes les queda más vida de la que les queda.
  const conNg = validos.filter((v) => num(v.ngCiclos) !== null);
  const conNf = validos.filter((v) => num(v.nfCiclos) !== null);
  const ngCompleto = validos.length > 0 && conNg.length === validos.length;
  const nfCompleto = validos.length > 0 && conNf.length === validos.length;

  const avisos = [];
  if (validos.length === 0) avisos.push("Todavía no hay vuelos registrados en el día.");
  if (anulados > 0) avisos.push(`${anulados} vuelo(s) anulado(s): no suman al total, pero quedan en el registro.`);
  if (validos.length > 0 && !ngCompleto) {
    avisos.push(`Faltan los ciclos NG en ${validos.length - conNg.length} de ${validos.length} vuelos: el total no se puede cerrar.`);
  }
  if (validos.length > 0 && !nfCompleto) {
    avisos.push(`Faltan los ciclos NF en ${validos.length - conNf.length} de ${validos.length} vuelos: el total no se puede cerrar.`);
  }
  const sinTiempo = validos.filter((v) => !Number.isFinite(v.ftMs) || v.ftMs <= 0).length;
  if (sinTiempo > 0) avisos.push(`${sinTiempo} vuelo(s) sin tiempo registrado.`);
  // El aviso distingue las dos razones por las que un vuelo no es medible:
  // que falte el dato, o que el dato exista pero sea imposible. Decir "faltan
  // datos en 0 de 2 vuelos" no le sirve a nadie.
  const sinDatoCombustible = validos.length - conConsumo.length;
  if (sinDatoCombustible > 0) {
    avisos.push(`Faltan datos de combustible en ${sinDatoCombustible} de ${validos.length} vuelos: el consumo del día no se puede totalizar.`);
  }
  if (consumosNegativos > 0) {
    avisos.push(
      `${consumosNegativos} vuelo(s) terminan con MÁS combustible del que tenían al arrancar. ` +
      "Esos vuelos NO entran en el total: con esos dos números no se puede medir cuánto se gastó. " +
      "Si se reabasteció con el motor en marcha, anotalo en observaciones y corregí el inicial; " +
      "si no, hay un dato mal cargado.",
    );
  }

  return {
    vuelos: validos.length,
    anulados,
    ...total,
    ft: aHoras(total.ftMs),
    at: aHoras(total.atMs),
    tierra: aHoras(total.tierraMs),
    ftHhMm: aHhMm(total.ftMs),
    atHhMm: aHhMm(total.atMs),
    tierraHhMm: aHhMm(total.tierraMs),
    // Redondeados a la centésima. Sumar 0,01 + 0,05 en coma flotante da
    // 0,060000000000000005, y eso salía tal cual en pantalla.
    ngCiclos: ngCompleto ? Math.round(conNg.reduce((s, v) => s + num(v.ngCiclos), 0) * 100) / 100 : null,
    nfCiclos: nfCompleto ? Math.round(conNf.reduce((s, v) => s + num(v.nfCiclos), 0) * 100) / 100 : null,
    ngCompleto,
    nfCompleto,
    // Solo se totaliza si están los datos de TODOS los vuelos: un consumo
    // parcial presentado como total del día haría creer que se gastó menos.
    combustibleConsumidoL: consumoCompleto ? Math.round(medibles.reduce((a, c) => a + c, 0) * 10) / 10 : null,
    consumoCompleto,
    consumosNegativos,
    // El día está listo para pasar a la bitácora solo si no falta nada.
    cerrable: validos.length > 0 && ngCompleto && nfCompleto && sinTiempo === 0,
    avisos,
  };
}

function aHoras(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.round((ms / 3600000) * 100) / 100;
}

function aHhMm(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.round(ms / 60000);
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
}

/**
 * Anula un vuelo del día. NO lo borra.
 *
 * Un vuelo que desaparece del registro no se puede auditar, y el motivo de por
 * qué no cuenta es justamente lo que un inspector viene a leer.
 */
export function anularVuelo(vuelos, id, motivo) {
  const texto = typeof motivo === "string" ? motivo.trim() : "";
  if (texto.length < 4) {
    return { vuelos, error: "Hace falta un motivo para anular un vuelo del día." };
  }
  return {
    vuelos: (vuelos || []).map((v) => (v.id === id
      ? { ...v, anulado: true, motivoAnulacion: texto, anuladoEl: new Date().toISOString() }
      : v)),
    error: null,
  };
}

/** Reactiva un vuelo anulado, conservando el motivo por el que se había anulado. */
export function reactivarVuelo(vuelos, id) {
  return (vuelos || []).map((v) => (v.id === id
    ? { ...v, anulado: false, motivoAnulacionPrevia: v.motivoAnulacion, motivoAnulacion: null }
    : v));
}
